import React, { useCallback, useState } from 'react';
import { X, Plus, Info, ChevronDown, ChevronRight } from 'lucide-react';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import OutputVariableMapping from '../../../../types/BotBuilder/OutputVariableMapping';

interface OutputMappingsProps {
  mappings: OutputVariableMapping[];
  onChange: (mappings: OutputVariableMapping[]) => void;
}

const generateId = (): string =>
  `mapping-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

function OutputMappings({
  mappings,
  onChange
}: Readonly<OutputMappingsProps>): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  const addMapping = useCallback(() => {
    onChange([
      ...mappings,
      { id: generateId(), jsonPath: '', intentVariable: '' }
    ]);
  }, [mappings, onChange]);

  const updateMapping = useCallback(
    (index: number, updates: Partial<OutputVariableMapping>) => {
      const updated = [...mappings];
      updated[index] = { ...updated[index], ...updates };
      onChange(updated);
    },
    [mappings, onChange]
  );

  const removeMapping = useCallback(
    (index: number) => {
      onChange(mappings.filter((_, i) => i !== index));
    },
    [mappings, onChange]
  );

  return (
    <div className='flex flex-col gap-2 overflow-hidden'>
      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={() => setIsExpanded(!isExpanded)}
          className='text-gray-500 hover:text-gray-700 p-0.5 -ml-1'
        >
          {isExpanded ? (
            <ChevronDown className='h-4 w-4' />
          ) : (
            <ChevronRight className='h-4 w-4' />
          )}
        </button>
        <Label className='text-sm font-medium'>Output mappings</Label>
        <CustomTooltip
          content={
            <>
              <p className='mb-1'>
                Save values from your returned dict to intent variables.
              </p>
              <p>
                If your code returns{' '}
                <code className='text-green-300'>
                  {'{'}
                  &apos;total&apos;: 42
                  {'}'}
                </code>
                {', set key to '}
                <code className='text-green-300'>total</code>
                {' to '}
                store 42 in an intent variable.
              </p>
            </>
          }
          sideOffset={5}
        >
          <button type='button' className='inline-flex'>
            <Info className='w-4 h-4 text-gray-400 cursor-help' />
          </button>
        </CustomTooltip>
      </div>
      {isExpanded &&
        mappings.map((mapping, index) => (
          <div
            key={mapping.id ?? `fallback-${index}`}
            className='flex flex-col gap-2 p-3 border rounded-lg bg-white relative'
          >
            <Button
              variant='ghost'
              size='icon'
              onClick={() => removeMapping(index)}
              className='h-6 w-6 absolute top-2 right-2 text-muted-foreground hover:text-destructive'
            >
              <X className='h-4 w-4' />
            </Button>
            <CustomInput
              label='Key'
              value={mapping.jsonPath}
              onChange={(e) =>
                updateMapping(index, { jsonPath: e.target.value })
              }
              placeholder='result'
              containerClassName='mb-0 pr-6'
              className='bg-gray-50'
            />
            <CustomInput
              label='Intent variable'
              value={mapping.intentVariable}
              onChange={(e) =>
                updateMapping(index, { intentVariable: e.target.value })
              }
              placeholder='variableName'
              containerClassName='mb-0'
              className='bg-gray-50'
            />
          </div>
        ))}
      {isExpanded && (
        <Button
          variant='outline'
          size='sm'
          onClick={addMapping}
          className='w-fit'
        >
          <Plus className='h-4 w-4 mr-1' />
          Add mapping
        </Button>
      )}
    </div>
  );
}

export default OutputMappings;
